import styled from 'styled-components';

import rem from 'polished/lib/helpers/rem';
import toColorString from 'polished/lib/color/toColorString';

import rgbaToHex from '@decisiv/ui-components/lib/utils/rgbaToHex';

import color from '@decisiv/design-tokens/lib/color';
import spacing from '@decisiv/design-tokens/lib/spacing';
import typography from '@decisiv/design-tokens/lib/typography';

// Real styled components rather than styled-components' `css` prop: portal's
// babel config has no babel-plugin-styled-components, so a `css={...}` would be
// dropped without warning. Same constraint ManagementLayout works under.

export const PageHeader = styled.div`
  background: ${toColorString(color.base.snowWhite)};
  border-bottom: 1px solid ${toColorString(color.base.halfMoon)};
  padding: ${rem(spacing.base * 2)} ${rem(spacing.base * 3)};
`;

// The table runs flush to the content area, as in the mockup — no inset card
// around it, so no padding here and no bordered panel.
export const PageBody = styled.div`
  flex: 1;
`;

// The mockup's CREATE USER split button: blue outline on white, filling pale
// blue while its menu is open. Styled by hand because KeyDS Button renders a
// single icon and this needs one on each side of the label — the same reason
// ManagementLayout/AppsLinks styles its own trigger.
export const CreateUserTrigger = styled.button`
  display: flex;
  align-items: center;
  gap: ${rem(spacing.base / 2)};
  padding: ${rem(spacing.base / 2)} ${rem(spacing.base)};
  border: 1px solid ${toColorString(color.interaction.pacificOcean)};
  border-radius: ${rem(2)};
  cursor: pointer;
  text-transform: uppercase;
  font-size: ${rem(typography.size.alias.paragraph2)};
  font-weight: ${typography.weight.alias.semibold};
  letter-spacing: ${rem(0.5)};
  color: ${toColorString(color.interaction.pacificOcean)};

  background-color: ${({ $active }) =>
    toColorString(
      $active ? color.interaction.arcticOcean40 : color.base.snowWhite,
    )};

  &:hover {
    background-color: ${toColorString(color.interaction.arcticOcean40)};
  }
`;

// The search box sits opposite the title in the header band, 400px wide in the
// design, with its hint beneath.
export const SearchArea = styled.div`
  width: ${rem(400)};
`;

export const SearchHint = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(spacing.base / 2)};
  margin-top: ${rem(spacing.base / 2)};
  color: ${toColorString(color.base.alaskanHusky)};
`;

// The filter row: 52px tall, chips inset 20px with 10px gaps, CLEAR ALL pushed to
// the far right against the same 20px gutter (Figma 787:78243).
//
// The band is tinted, not white — fullMoon at 50% over the white page, which is
// the #f7f8fb the mockup uses. It sits between the white header above and the
// halfMoon table header below.
export const FilterBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(spacing.base)};
  padding: ${rem(spacing.base)} ${rem(spacing.base * 2)};
  background: ${rgbaToHex(color.opacity.fullMoon50, color.base.snowWhite)};
  border-bottom: 1px solid ${toColorString(color.base.halfMoon)};
`;

export const FilterBarSpacer = styled.div`
  flex: 1;
`;

// Every filter menu is 300px wide in the design.
export const FilterPanel = styled.div`
  width: ${rem(300)};
`;

export const FilterPanelTitle = styled.div`
  padding: ${rem(spacing.base)};
`;

export const FilterSearchRow = styled.div`
  margin-top: ${rem(spacing.base / 2)};
`;

export const FilterPanelDivider = styled.div`
  border-top: 1px solid ${toColorString(color.opacity.charcoal15)};
`;

// Matches AccountsField's AccountsStatus, since it says the same thing in the
// same place.
export const FilterPanelStatus = styled.div`
  padding: ${rem(spacing.base / 2)} ${rem(spacing.base)};
  border-top: 1px solid ${toColorString(color.opacity.charcoal15)};
`;

export const UserIdentity = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(spacing.base)};
`;

export const UserNames = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;

  > * {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

// Stands in for the table's header row in the empty state, so the content area
// still reads as a table that happens to have nothing in it.
export const TableDivider = styled.div`
  border-top: 1px solid ${toColorString(color.base.halfMoon)};
`;

// The Figma Message instance is 340px wide, so the body copy wraps rather than
// running out as one line.
export const EmptyStateArea = styled.div`
  display: flex;
  justify-content: center;
  padding: ${rem(spacing.base * 4)} 0;

  > * {
    max-width: ${rem(340)};
  }
`;

export const StateContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${rem(spacing.base * 4)} 0;
`;

// PD-4677: the access tabs, the ACTIONS cell's kebab menu, and the Remove /
// Restore Access modal body.

export const TabsArea = styled.div`
  padding: 0 ${rem(spacing.base * 2)};
`;

// Right-aligned so the kebab sits under the ACTIONS heading rather than against
// the previous column, which is where the design puts it.
export const RowActionsCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

export const MenuTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${rem(spacing.base / 2)};
  background: none;
  border: none;
  border-radius: ${rem(2)};
  color: ${toColorString(color.interaction.pacificOcean)};
  cursor: pointer;
  line-height: 0;

  &:hover {
    background-color: ${toColorString(color.opacity.fullMoon50)};
  }

  &:focus-visible {
    outline: 1px solid ${toColorString(color.interaction.pacificOcean)};
    outline-offset: ${rem(2)};
  }
`;

export const MenuList = styled.div`
  display: flex;
  flex-direction: column;
  min-width: ${rem(173)};
`;

// A divider between items rather than around them, matching the design's single
// rule between View Details and the access action.
export const MenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: ${rem(spacing.base)};
  width: 100%;
  padding: ${rem(spacing.base / 2)} ${rem(spacing.base)};
  background: none;
  border: none;
  color: ${toColorString(color.interaction.pacificOcean)};
  cursor: pointer;
  font-size: ${rem(typography.size.alias.default)};
  font-weight: ${typography.weight.alias.medium};
  text-align: left;
  text-decoration: none;
  white-space: nowrap;

  & + & {
    border-top: 1px solid
      ${rgbaToHex(color.opacity.charcoal15, color.base.snowWhite)};
  }

  &:hover {
    background-color: ${toColorString(color.opacity.fullMoon50)};
  }
`;

export const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(spacing.base * 2)};
`;

export const ContactCard = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(spacing.base)};
  padding: ${rem(spacing.base)};
  background-color: ${rgbaToHex(
    color.opacity.fullMoon50,
    color.base.snowWhite,
  )};
`;

export const ContactDetails = styled.div`
  display: flex;
  flex-direction: column;
`;
